import { ref, type Ref } from 'vue'
import { CLIPBOARD_MARKER } from '../model/constants'
import { centerNodeAt, createNode } from '../model/nodeFactory'
import type {
  ClipboardPayload,
  FlowEdge,
  FlowNode,
  GroupOption,
  NodeType,
  PaletteItem,
  Point,
} from '../model/types'
import { clone, type FlowDocument } from './useFlowDocument'
import type { FlowHistory } from './useFlowHistory'
import type { FlowSelection } from './useFlowSelection'
import type { FlowViewport } from './useFlowViewport'

type Deps = {
  document: FlowDocument
  selection: FlowSelection
  history: FlowHistory
  viewport: FlowViewport
  groupOptions: Ref<GroupOption[]>
  typeLabel: (type: NodeType) => string
}

/** System clipboard support enables copy/paste between editor windows and tabs. */
const writeSystemClipboard = (payload: ClipboardPayload) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return
  void navigator.clipboard.writeText(CLIPBOARD_MARKER + JSON.stringify(payload))
}

const readSystemClipboard = async (): Promise<ClipboardPayload | null> => {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) return null
  try {
    const text = await navigator.clipboard.readText()
    if (!text.startsWith(CLIPBOARD_MARKER)) return null
    const raw = JSON.parse(text.slice(CLIPBOARD_MARKER.length)) as unknown
    const kind = (raw as ClipboardPayload | null)?.kind
    return raw !== null && typeof raw === 'object' && (kind === 'nodes' || kind === 'edge')
      ? (raw as ClipboardPayload)
      : null
  } catch {
    return null
  }
}

/** Graph mutations triggered by the palette, toolbar, keyboard and inspector. */
export function useFlowEditing({
  document,
  selection,
  history,
  viewport,
  groupOptions,
  typeLabel,
}: Deps) {
  const { nodes, edges, nodeById } = document
  const clipboard = ref<ClipboardPayload | null>(null)

  const newNode = (type: NodeType, position?: Point) =>
    createNode(
      document.nextNodeId(),
      type,
      position ?? viewport.viewportCenter(),
      groupOptions.value[0]?.id || 'group-0',
    )

  const addNodeAt = (type: NodeType, position?: Point) => {
    history.runMutation(`Add ${typeLabel(type)}`, () => {
      const node = newNode(type, position)
      nodes.value.push(node)
      selection.selectNode(node.id)
    })
  }

  const addNodeFromPalette = (item: PaletteItem, position?: Point) => {
    if (item.singleton && nodes.value.some((n) => n.type === item.type)) return
    history.runMutation(`Add ${item.label}`, () => {
      const node = newNode(item.type)
      if (item.defaultLabel) node.label = item.defaultLabel
      if (item.defaultData) node.data = { ...node.data, ...item.defaultData }
      if (item.id === 'show-media') {
        // Helpful defaults without reserving names: a user may still rename two
        // media nodes alike because their IDs, not their labels, identify media.
        const count = nodes.value.filter(
          (n) => n.type === 'action' && n.data.actionType === 'show-media',
        ).length
        node.label = `Media ${count + 1}`
      }
      if (position) centerNodeAt(node, position)
      nodes.value.push(node)
      selection.selectNode(node.id)
    })
  }

  const removeNode = (nodeId: string) => {
    if (!nodeById.value.get(nodeId)) return
    history.runMutation('Delete node', () => {
      nodes.value = nodes.value.filter((item) => item.id !== nodeId)
      edges.value = edges.value.filter(
        (edge) => edge.fromNodeId !== nodeId && edge.toNodeId !== nodeId,
      )
      if (selection.selectedNodeIds.value.includes(nodeId)) {
        selection.selectNodes(selection.selectedNodeIds.value.filter((id) => id !== nodeId))
      }
    })
  }

  const removeSelectedEdge = () => {
    const edgeId = selection.selectedEdgeId.value
    if (!edgeId) return
    history.runMutation('Delete connection', () => {
      edges.value = edges.value.filter((edge) => edge.id !== edgeId)
      selection.selectedEdgeId.value = null
    })
  }

  /** Deletes the selected edge or nodes (with their connections). */
  const removeSelected = () => {
    if (selection.selectedEdgeId.value) {
      removeSelectedEdge()
      return
    }
    if (selection.selectedNodeIds.value.length === 0) return
    history.runMutation('Delete nodes', () => {
      const selectedIds = new Set(selection.selectedNodeIds.value)
      nodes.value = nodes.value.filter((node) => !selectedIds.has(node.id))
      edges.value = edges.value.filter(
        (edge) => !selectedIds.has(edge.fromNodeId) && !selectedIds.has(edge.toNodeId),
      )
      selection.clearSelection()
    })
  }

  const selectedNodesWithInternalEdges = () => {
    const selected = new Set(selection.selectedNodeIds.value)
    return {
      nodes: nodes.value.filter((node) => selected.has(node.id)),
      // Only edges with both endpoints inside the selection travel with it.
      edges: edges.value.filter((e) => selected.has(e.fromNodeId) && selected.has(e.toNodeId)),
    }
  }

  const copySelected = () => {
    if (selection.selectedNodeIds.value.length > 0) {
      const picked = selectedNodesWithInternalEdges()
      clipboard.value = {
        kind: 'nodes',
        nodes: clone(picked.nodes),
        internalEdges: clone(picked.edges),
      }
      writeSystemClipboard(clipboard.value)
      return
    }
    const edge = edges.value.find((item) => item.id === selection.selectedEdgeId.value)
    if (selection.selectedEdgeId.value && edge) {
      clipboard.value = { kind: 'edge', edge: clone(edge) }
      writeSystemClipboard(clipboard.value)
    }
  }

  const pushEdgeCopy = (source: FlowEdge) => {
    const next = clone(source)
    next.id = document.nextEdgeId()
    edges.value.push(next)
    selection.selectEdge(next.id)
  }

  const duplicateSelected = () => {
    if (selection.selectedNodeIds.value.length > 0) {
      history.runMutation('Duplicate nodes', () => {
        const selected = new Set(selection.selectedNodeIds.value)
        const addedIds: string[] = []
        for (const source of nodes.value.filter((node) => selected.has(node.id))) {
          const next = clone(source)
          next.id = document.nextNodeId()
          next.locked = false
          next.x += 42
          next.y += 42
          next.label = `${source.label} Copy`
          nodes.value.push(next)
          addedIds.push(next.id)
        }
        selection.selectNodes(addedIds)
      })
      return
    }
    if (selection.selectedEdgeId.value) {
      history.runMutation('Duplicate connection', () => {
        const source = edges.value.find((item) => item.id === selection.selectedEdgeId.value)
        if (source) pushEdgeCopy(source)
      })
    }
  }

  /** Pasted nodes keep their layout, centred on the cursor; internal edges are remapped. */
  const pasteNodes = (sources: FlowNode[], internalEdges: FlowEdge[]) => {
    const idMap = new Map<string, string>()
    const addedIds: string[] = []
    const xs = sources.map((n) => n.x)
    const ys = sources.map((n) => n.y)
    const dx = viewport.cursorWorld.value.x - (Math.min(...xs) + Math.max(...xs)) / 2
    const dy = viewport.cursorWorld.value.y - (Math.min(...ys) + Math.max(...ys)) / 2
    for (const source of sources) {
      const next = clone(source)
      next.id = document.nextNodeId()
      next.locked = false
      next.x = Math.round(source.x + dx)
      next.y = Math.round(source.y + dy)
      idMap.set(source.id, next.id)
      nodes.value.push(next)
      addedIds.push(next.id)
    }
    for (const sourceEdge of internalEdges) {
      const from = idMap.get(sourceEdge.fromNodeId)
      const to = idMap.get(sourceEdge.toNodeId)
      if (!from || !to) continue
      edges.value.push({
        ...clone(sourceEdge),
        id: document.nextEdgeId(),
        fromNodeId: from,
        toNodeId: to,
      })
    }
    selection.selectNodes(addedIds)
  }

  const pasteSelected = async () => {
    // Prefer the system clipboard so paste works across windows/tabs.
    const systemPayload = await readSystemClipboard()
    if (systemPayload) clipboard.value = systemPayload
    if (!clipboard.value) return
    history.runMutation('Paste', () => {
      const payload = clipboard.value
      if (payload?.kind === 'nodes') {
        pasteNodes(payload.nodes, payload.internalEdges)
        return
      }
      if (payload?.kind === 'edge') {
        const source = payload.edge
        if (!nodeById.value.has(source.fromNodeId) || !nodeById.value.has(source.toNodeId)) return
        pushEdgeCopy(source)
      }
    })
  }

  /** Arrow-key nudge; locked nodes stay put. */
  const nudgeNodes = (ids: string[], dx: number, dy: number) => {
    history.runMutation('Move nodes', () => {
      nodes.value = nodes.value.map((n) => {
        if (!ids.includes(n.id) || n.locked) return n
        return { ...n, x: Math.round(n.x + dx), y: Math.round(n.y + dy) }
      })
    })
  }

  return {
    clipboard,
    addNodeAt,
    addNodeFromPalette,
    removeNode,
    removeSelectedEdge,
    removeSelected,
    copySelected,
    duplicateSelected,
    pasteSelected,
    nudgeNodes,
  }
}

export type FlowEditing = ReturnType<typeof useFlowEditing>
